import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LightTooltip } from "@/components/LightTooltip";
import {
  useCompanyFilterActions,
  useCompanyFilterOptions,
  useCompanyFilterSlice,
} from "../../hooks";
import { AccordionSection } from "../../components/AccordionSection";
import { defaultTargetOrganizationsSlice, type CachedCompany } from "./slice";
import type { TargetOrganizationsOptions } from "./options";
import { getActiveIds, uniqueIds } from "./utils";

const SEARCH_CONSTANTS = {
  MIN_FILTER_LENGTH: 3,
  DEBOUNCE_DELAY: 300,
  DEFAULT_PLACEHOLDER: "Search by name, domain, or DUNS...",
  MIN_CHAR_MESSAGE: "Enter at least 3 characters",
};

function readSessionBool(key: string, fallback: boolean): boolean {
  if (typeof window === "undefined") return fallback;
  const raw = sessionStorage.getItem(key);
  if (raw === null) return fallback;
  return raw === "true";
}

function writeSessionBool(key: string, value: boolean): void {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(key, String(value));
}

function useCompanyFilterAccordionState(
  storagePrefix: string,
  defaults?: { selectedDefault?: boolean; searchDefault?: boolean }
) {
  const selectedDefault = defaults?.selectedDefault ?? false;
  const searchDefault = defaults?.searchDefault ?? true;
  const [selectedExpanded, setSelectedExpanded] = useState(() =>
    readSessionBool(`${storagePrefix}.selectedExpanded`, selectedDefault)
  );
  const [searchExpanded, setSearchExpanded] = useState(() =>
    readSessionBool(`${storagePrefix}.searchExpanded`, searchDefault)
  );

  // Re-sync accordion states when segment scope changes.
  useEffect(() => {
    setSelectedExpanded(
      readSessionBool(`${storagePrefix}.selectedExpanded`, selectedDefault)
    );
    setSearchExpanded(
      readSessionBool(`${storagePrefix}.searchExpanded`, searchDefault)
    );
  }, [storagePrefix, selectedDefault, searchDefault]);

  // Persist selected accordion state to session storage.
  useEffect(() => {
    writeSessionBool(`${storagePrefix}.selectedExpanded`, selectedExpanded);
  }, [storagePrefix, selectedExpanded]);

  // Persist search accordion state to session storage.
  useEffect(() => {
    writeSessionBool(`${storagePrefix}.searchExpanded`, searchExpanded);
  }, [storagePrefix, searchExpanded]);

  return {
    selectedExpanded,
    setSelectedExpanded,
    searchExpanded,
    setSearchExpanded,
  };
}

export const TargetOrganizationsFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("targetOrganizations");
  const { setSlice } = useCompanyFilterActions();
  const options = useCompanyFilterOptions("targetOrganizations") as
    | TargetOrganizationsOptions
    | undefined;

  const storagePrefix = "targetOrganizations";

  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState<CachedCompany[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const {
    selectedExpanded,
    setSelectedExpanded,
    searchExpanded,
    setSearchExpanded,
  } = useCompanyFilterAccordionState(storagePrefix, {
    selectedDefault: false,
    searchDefault: true,
  });

  const activeIds = useMemo(() => getActiveIds(slice), [slice]);
  const hasAccounts = activeIds.length > 0;
  const includeCorporateFamily =
    slice.hgIdsIncludingCorporateRelatives.length > 0;
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Autofocus search input when the search accordion opens.
  useEffect(() => {
    if (!searchExpanded) return;
    const rafId = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(rafId);
  }, [searchExpanded]);

  const cachedById = useMemo(() => {
    return new Map(slice.companiesCache.map((c) => [c.hgId, c]));
  }, [slice.companiesCache]);

  const selectedSet = useMemo(() => new Set(activeIds), [activeIds]);

  // Derive loading state from cache completeness
  // (hydration is handled by useTargetOrganizationsHydration in the controller)
  const isLoadingCompanies =
    activeIds.length > 0 && !activeIds.every((id) => cachedById.has(id));

  const searchCompanies = options?.onSearch;

  const searchRequestRef = useRef<string | null>(null);
  // Debounced search with stale-request protection.
  useEffect(() => {
    const trimmed = searchText.trim();
    if (trimmed.length < SEARCH_CONSTANTS.MIN_FILTER_LENGTH) {
      setSearchResults([]);
      setIsSearching(false);
      searchRequestRef.current = null;
      return;
    }

    setIsSearching(true);
    const timeout = setTimeout(() => {
      const requestKey = trimmed;
      searchRequestRef.current = requestKey;

      if (!searchCompanies) {
        setSearchResults([]);
        setIsSearching(false);
        searchRequestRef.current = null;
        return;
      }

      searchCompanies(trimmed)
        .then((companies) => {
          if (searchRequestRef.current !== requestKey) return;
          setSearchResults(companies);
        })
        .finally(() => {
          if (searchRequestRef.current === requestKey) setIsSearching(false);
        });
    }, SEARCH_CONSTANTS.DEBOUNCE_DELAY);

    return () => clearTimeout(timeout);
  }, [searchCompanies, searchText]);

  const handleClearAll = useCallback(() => {
    setSlice("targetOrganizations", defaultTargetOrganizationsSlice);
  }, [setSlice]);

  const toggleCompany = useCallback(
    (company: CachedCompany, checked: boolean) => {
      const newIds = checked
        ? uniqueIds([...activeIds, company.hgId])
        : activeIds.filter((id) => id !== company.hgId);

      const cacheById = new Map<string, CachedCompany>();
      slice.companiesCache.forEach((c) => cacheById.set(c.hgId, c));
      if (checked) cacheById.set(company.hgId, company);
      const nextCache = newIds
        .map((id) => cacheById.get(id))
        .filter((c): c is CachedCompany => !!c);

      setSlice("targetOrganizations", {
        hgIds: includeCorporateFamily ? [] : newIds,
        hgIdsIncludingCorporateRelatives: includeCorporateFamily ? newIds : [],
        companiesCache: nextCache,
      });
    },
    [activeIds, includeCorporateFamily, setSlice, slice.companiesCache]
  );

  const setIds = useCallback(
    (ids: string[], nextIncludeCorporateFamily: boolean) => {
      const unique = uniqueIds(ids);
      setSlice("targetOrganizations", {
        hgIds: nextIncludeCorporateFamily ? [] : unique,
        hgIdsIncludingCorporateRelatives: nextIncludeCorporateFamily
          ? unique
          : [],
        companiesCache: slice.companiesCache.filter((c) =>
          unique.includes(c.hgId)
        ),
      });
    },
    [setSlice, slice.companiesCache]
  );

  const handleRemoveAccount = useCallback(
    (hgId: string) => {
      const newIds = activeIds.filter((id) => id !== hgId);
      setIds(newIds, includeCorporateFamily);
    },
    [activeIds, includeCorporateFamily, setIds]
  );

  const handleCorporateFamilyChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIds(activeIds, event.target.checked);
    },
    [activeIds, setIds]
  );

  const showSearchHint =
    searchText.trim().length > 0 &&
    searchText.trim().length < SEARCH_CONSTANTS.MIN_FILTER_LENGTH;

  const resultsExcludingSelected = useMemo(() => {
    if (searchResults.length === 0) return searchResults;
    return searchResults.filter((c) => !selectedSet.has(c.hgId));
  }, [searchResults, selectedSet]);

  return (
    <Box sx={{ mx: -2, mt: -2 }}>
      {/* Selected Companies Accordion - auto-controlled */}
      <AccordionSection
        title="Selected"
        id="company-search-selected-section"
        expanded={selectedExpanded}
        onToggle={() => setSelectedExpanded((prev) => !prev)}
        badge={activeIds.length}
        titleAdornment={
          hasAccounts ? (
            <IconButton
              id="company-search-clear-all-btn"
              size="small"
              aria-label="Clear selected companies"
              onClick={(event) => {
                event.stopPropagation();
                handleClearAll();
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          ) : null
        }
        action={
          hasAccounts && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    id="company-search-corporate-family-toggle"
                    checked={includeCorporateFamily}
                    onChange={handleCorporateFamilyChange}
                    size="small"
                  />
                }
                label={
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption">Corporate Family</Typography>
                    <LightTooltip title="Include parent and subsidiaries when filtering">
                      <IconButton
                        size="small"
                        aria-label="Corporate family help"
                        disableRipple
                        sx={{ p: 0.25 }}
                      >
                        <InfoOutlinedIcon
                          fontSize="inherit"
                          sx={{ fontSize: 16 }}
                        />
                      </IconButton>
                    </LightTooltip>
                  </Stack>
                }
                sx={{ mr: 0, "& .MuiFormControlLabel-label": { ml: 0.5 } }}
              />
            </Stack>
          )
        }
      >
        {isLoadingCompanies ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading selected companies...
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={0} id="company-search-selected-list">
            {activeIds.map((id) => {
              const company = cachedById.get(id);
              if (!company) return null;
              return (
                <Box
                  key={company.hgId}
                  id={`company-search-selected-${company.hgId}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    mx: -2,
                    px: 2,
                    py: 0,
                    "&:hover": { bgcolor: "grey.50" },
                    "& .remove-button": {
                      opacity: 0,
                      transition: "opacity 0.15s ease",
                    },
                    "&:hover .remove-button, & .remove-button:focus-visible": {
                      opacity: 1,
                    },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2">{company.name}</Typography>
                  </Box>
                  <IconButton
                    id={`company-search-remove-${company.hgId}-btn`}
                    size="small"
                    className="remove-button"
                    aria-label={`Remove ${company.name}`}
                    onClick={() => handleRemoveAccount(company.hgId)}
                    sx={{ ml: 1 }}
                    disableRipple
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Stack>
        )}

        {!hasAccounts && (
          <Typography variant="body2" color="text.secondary">
            No companies selected
          </Typography>
        )}
      </AccordionSection>

      {/* Search Companies Accordion */}
      <AccordionSection
        title="Search"
        id="company-search-search-section"
        expanded={searchExpanded}
        onToggle={() => setSearchExpanded((prev) => !prev)}
      >
        <TextField
          id="company-search-input"
          placeholder={SEARCH_CONSTANTS.DEFAULT_PLACEHOLDER}
          size="small"
          fullWidth
          inputRef={searchInputRef}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                </InputAdornment>
              ),
            },
          }}
          sx={{ mb: 1 }}
        />

        {showSearchHint && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {SEARCH_CONSTANTS.MIN_CHAR_MESSAGE}
          </Typography>
        )}

        {searchText.trim().length >= SEARCH_CONSTANTS.MIN_FILTER_LENGTH && (
          <Box>
            {isSearching && (
              <Stack
                id="company-search-loading"
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Searching companies...
                </Typography>
              </Stack>
            )}

            {!isSearching && resultsExcludingSelected.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No companies found
              </Typography>
            )}

            {!isSearching && (
              <Stack spacing={0} id="company-search-results-list">
                {resultsExcludingSelected.map((company) => (
                  <Box
                    key={company.hgId}
                    id={`company-search-result-${company.hgId}`}
                    onClick={() => toggleCompany(company, true)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      py: 0.75,
                      px: 2,
                      mx: -2,
                      cursor: "pointer",
                      borderRadius: 1,
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" noWrap>
                        {company.name}
                      </Typography>
                      {company.url && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          component="div"
                        >
                          {company.url}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </AccordionSection>
    </Box>
  );
});

TargetOrganizationsFilter.displayName = "TargetOrganizationsFilter";
